import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  ClinicalPageShell,
  PatientStrip,
  ClosedLoopTracker,
  StatusPill,
  tokens,
} from "../../shared/clinical-design";
import {
  APPT_STATUS,
  APPT_TYPES,
  ENCOUNTER_TABS,
  NOTE_STATUS,
  CHARGE_STATUS,
  CDCI_BASE_PATH,
} from "../common/constants";
import * as cdci from "../services/cdciAmdService";
import ContextTab from "../components/encounter/ContextTab";
import NoteTab from "../components/encounter/NoteTab";
import ChargesTab from "../components/encounter/ChargesTab";
import SignTab from "../components/encounter/SignTab";

/**
 * CDCI — Encounter Workspace (páginas 4.2 a 4.5 del documento maestro).
 * Una sola superficie de trabajo para el encuentro: contexto del paciente,
 * documentación de la nota, captura de cargos y firma/cierre, con el
 * journey como Closed-Loop Tracker siempre visible.
 */
export default function CdciEncounterPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(ENCOUNTER_TABS.CONTEXT);
  const [note, setNote] = useState(null);
  const [charges, setCharges] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ctx = await cdci.getEncounterContext(appointmentId);
      setContext(ctx);
      setNote(ctx.note);
      setCharges(ctx.charges);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const appt = context?.appointment;
  const patient = context?.patient;
  const isSigned = note?.status === NOTE_STATUS.SIGNED.key;
  const chargesCaptured = charges?.status === CHARGE_STATUS.CAPTURED.key;

  // Journey (§2): el tracker refleja el avance real del encuentro.
  const journeySteps = useMemo(() => {
    const noteState = isSigned ? "done" : note ? "active" : "pending";
    return [
      { label: "Schedule", state: "done" },
      { label: "Patient Context", state: context ? "done" : "pending" },
      { label: "Documentation", state: noteState },
      { label: "Charge Capture", state: chargesCaptured ? "done" : charges ? "active" : "pending" },
      { label: "Sign & Close", state: isSigned ? "done" : "pending" },
    ];
  }, [context, note, charges, isSigned, chargesCaptured]);

  const handleStartNote = async (templateName) => {
    const created = await cdci.createNote({ appointmentId, templateName });
    setNote(created);
    setTab(ENCOUNTER_TABS.NOTE);
  };

  if (error) {
    return (
      <ClinicalPageShell title="Encounter" moduleTag="CDCI" devPreview>
        <Typography sx={{ color: tokens.crit, fontSize: 14 }}>{error}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(CDCI_BASE_PATH)} sx={{ mt: 2 }}>
          Back to My Schedule
        </Button>
      </ClinicalPageShell>
    );
  }

  const apptType = appt ? APPT_TYPES[appt.typeKey] : null;
  const apptStatus = appt ? APPT_STATUS[appt.statusKey] || APPT_STATUS.SCHEDULED : null;

  return (
    <ClinicalPageShell
      title={appt ? `Encounter — ${appt.time} · ${apptType?.label || ""}` : "Encounter"}
      moduleTag="CDCI"
      devPreview
      actions={
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(CDCI_BASE_PATH)}
          sx={{ color: tokens.ink2, textTransform: "none" }}
        >
          My Schedule
        </Button>
      }
    >
      {patient && (
        <PatientStrip
          patient={{
            name: patient.name,
            age: patient.age,
            gender: patient.gender,
            cipId: patient.cipId,
            location: appt?.location?.name,
            pcp: patient.pcp,
            membership: patient.membership,
            sensitive: patient.sensitive,
          }}
          actions={apptStatus && <StatusPill tone={apptStatus.tone} label={apptStatus.label} />}
          sx={{ mb: 1.5 }}
        />
      )}

      <Box
        sx={{
          bgcolor: tokens.surface,
          border: `1px solid ${tokens.line}`,
          borderRadius: "6px",
          px: 2,
          py: 1,
          mb: 1.5,
        }}
      >
        <ClosedLoopTracker steps={journeySteps} />
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{
          minHeight: 36,
          mb: 1.5,
          "& .MuiTab-root": {
            minHeight: 36,
            textTransform: "none",
            fontFamily: tokens.fontUI,
            fontSize: 13.5,
            color: tokens.ink2,
          },
          "& .Mui-selected": { color: `${tokens.primary} !important`, fontWeight: 600 },
          "& .MuiTabs-indicator": { bgcolor: tokens.primary },
        }}
      >
        <Tab value={ENCOUNTER_TABS.CONTEXT} label="Patient Context" />
        <Tab value={ENCOUNTER_TABS.NOTE} label="Documentation" />
        <Tab value={ENCOUNTER_TABS.CHARGES} label="Charge Capture" />
        <Tab value={ENCOUNTER_TABS.SIGN} label="Sign & Close" />
      </Tabs>

      {tab === ENCOUNTER_TABS.CONTEXT && (
        <ContextTab
          loading={loading}
          context={context}
          note={note}
          onStartNote={() => setTab(ENCOUNTER_TABS.NOTE)}
        />
      )}
      {tab === ENCOUNTER_TABS.NOTE && (
        <NoteTab
          appointment={appt}
          note={note}
          onCreate={handleStartNote}
          onNoteSaved={setNote}
          readOnly={isSigned}
        />
      )}
      {tab === ENCOUNTER_TABS.CHARGES && (
        <ChargesTab
          appointment={appt}
          patient={patient}
          note={note}
          charges={charges}
          onChargesChanged={setCharges}
          readOnly={isSigned}
        />
      )}
      {tab === ENCOUNTER_TABS.SIGN && (
        <SignTab
          appointment={appt}
          note={note}
          charges={charges}
          onSigned={(signedNote) => {
            setNote(signedNote);
            load();
          }}
        />
      )}
    </ClinicalPageShell>
  );
}
